import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useCancelNegotiation } from "../../hooks/useCancelNegotiation";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { NegotiationData } from "../../types/negotiations.type";
import TextArea from "@/common/components/TextArea";
import { SubmitHandler, useForm } from "react-hook-form";
import { cancelNegotiationSchema, CancelNegotiationValidationSchema } from "../../schemas/cancelNegotiation.schema";
import { zodResolver } from "@hookform/resolvers/zod";

interface CancelNegotiationModalProps {
  negotiationStatus?: NegotiationData['status'];
}

const CancelNegotiationModal = ({ negotiationStatus }: CancelNegotiationModalProps) => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isOpen, setIsOpen] = useState(false);

  const {
    mutate: cancelNegotiation,
    isPending: cancelNegotiationIsPending,
    isError: cancelNegotiationFailed,
  } = useCancelNegotiation({
    id,
    onSuccess: () => {
      setIsOpen(false);
      setMessages(['Negociação cancelada com sucesso']);
      queryClient.invalidateQueries({ queryKey: ['negotiations', id] });
    },
    onError: (error) => {
      setMessages(error.response?.data.errors?.base || ['Ocorreu um erro ao cancelar negociação']);
    },
  });

  const { handleSubmit, register, formState: { errors } } = useForm<CancelNegotiationValidationSchema>({
    resolver: zodResolver(cancelNegotiationSchema),
  });

  const onSubmit: SubmitHandler<CancelNegotiationValidationSchema> = (data) => cancelNegotiation(data);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <>
            {['awaiting_establishment_response', 'awaiting_client_response', 'accepted'].includes(negotiationStatus || '') && (
              <Button variant="error">
                Cancelar negociação
              </Button>
            )}
          </>
        )}
        title="Cancelar negociação"
        subtitle={(
          <div className="flex flex-col gap-2 text-white-400">
            <p>Essa ação não é uma contraproposta, e não poderá ser desfeita, perdendo todo o processo de negociação.</p>

            {negotiationStatus === 'awaiting_client_response' && (
              <p>Caso deseje fazer uma contraproposta, clique em voltar, e escolha o produto para propor um novo preço.</p>
            )}
          </div>
        )}
        body={(
          <div className="flex flex-col gap-4 text-white-800">
            <TextArea
              label="Motivo"
              markAsRequired
              rows={4}
              {...register('cancel_reason')}
              errorMessage={errors.cancel_reason?.message || undefined}
            />
          </div>
        )}
        useCancellableFooter
        cancelButtonText="Voltar"
        confirmButtonText="Cancelar negociação"
        cancelButtonClassName="bg-black-900"
        titleClass="text-2xl"
        subtitleClassName="mt-1 text-white-400"
        className="rounded-lg overflow-hidden"
        onConfirm={handleSubmit(onSubmit)}
        disabled={cancelNegotiationIsPending}
        confirmButtonIsLoading={cancelNegotiationIsPending}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={cancelNegotiationFailed}
        />
      )}
    </>
  );
};

export default CancelNegotiationModal;
